import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Home, Stethoscope, MapPin, Bot, HandHeart } from 'lucide-react'

/**
 * Mobile-first bottom action bar for the public portal.
 *
 * The portal header collapses to a hamburger on mobile, which buries every
 * primary action behind a drawer. On a phone — the dominant access device for
 * a public-health portal — the things a visitor most likely wants to do right
 * now should be one thumb-tap away. This fixed bottom bar surfaces the five
 * highest-value public-health actions (Home, Symptom Checker, Find a Centre,
 * TB Assistant, Get Help) directly under the thumb.
 *
 * Design notes:
 * - `lg:hidden` — desktop keeps the full in-page header nav; the bar is mobile-only.
 * - Each item is a router <Link> with a ≥44px touch target (WCAG 2.2 2.5.8 /
 *   5.8 target-spacing) and `aria-current="page"` on the active destination.
 * - `env(safe-area-inset-bottom)` padding keeps the bar clear of the iPhone home
 *   indicator / notched devices (Phone Safari compatibility).
 * - `backdrop-blur` + semi-opaque ground keeps content readable as the page
 *   scrolls behind it, without a heavy opaque bar.
 * - The bar is a <nav aria-label> landmark so screen-reader users can jump to it.
 */
export function PortalBottomNav() {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const items = useMemo(
    () => [
      { icon: Home, label: t('common.home', 'Home'), path: '/' },
      { icon: Stethoscope, label: t('nav.symptomChecker', 'Symptom Checker'), path: '/symptom-checker' },
      { icon: MapPin, label: t('nav.findTbCentre', 'Find a Centre'), path: '/find-tb-centre' },
      { icon: Bot, label: t('nav.assistant', 'TB Assistant'), path: '/assistant' },
      { icon: HandHeart, label: t('nav.getHelp', 'Get Help'), path: '/get-help' },
    ],
    [t]
  )

  const isActive = (path: string) =>
    path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(path)

  return (
    <nav
      aria-label={t('header.bottomNavAria', 'Primary actions')}
      className="xl:hidden fixed bottom-0 left-0 right-0 z-40 w-full overflow-x-hidden bg-white/95 backdrop-blur-sm border-t border-snow-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex w-full max-w-6xl mx-auto">
        {items.map((it) => {
          const Icon = it.icon
          const active = isActive(it.path)
          return (
            <li key={it.path} className="flex-1 min-w-0">
              <Link
                to={it.path}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center justify-center gap-0.5 py-1.5 min-h-[52px] text-[10px] font-medium transition-colors min-w-0 ${
                  active ? 'text-[rgb(15_76_129)]' : 'text-snow-600'
                }`}
              >
                <Icon className="w-5 h-5" aria-hidden="true" />
                <span className="leading-tight text-center px-0.5 w-full break-words">{it.label}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}